import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { vendorProfileService, VendorProfile } from '../../services/vendorProfileService';

// Common country codes for phone numbers
const countryCodes = [
  { code: 'US', name: 'United States (+1)', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada (+1)', flag: '🇨🇦' },
  { code: 'GB', name: 'United Kingdom (+44)', flag: '🇬🇧' },
  { code: 'EG', name: 'Egypt (+20)', flag: '🇪🇬' },
  { code: 'AE', name: 'United Arab Emirates (+971)', flag: '🇦🇪' },
  { code: 'SA', name: 'Saudi Arabia (+966)', flag: '🇸🇦' },
  { code: 'KW', name: 'Kuwait (+965)', flag: '🇰🇼' },
  { code: 'QA', name: 'Qatar (+974)', flag: '🇶🇦' },
  { code: 'BH', name: 'Bahrain (+973)', flag: '🇧🇭' },
  { code: 'OM', name: 'Oman (+968)', flag: '🇴🇲' },
  { code: 'JO', name: 'Jordan (+962)', flag: '🇯🇴' },
  { code: 'LB', name: 'Lebanon (+961)', flag: '🇱🇧' },
  { code: 'FR', name: 'France (+33)', flag: '🇫🇷' },
  { code: 'DE', name: 'Germany (+49)', flag: '🇩🇪' },
  { code: 'IT', name: 'Italy (+39)', flag: '🇮🇹' },
  { code: 'ES', name: 'Spain (+34)', flag: '🇪🇸' },
  { code: 'AU', name: 'Australia (+61)', flag: '🇦🇺' },
  { code: 'IN', name: 'India (+91)', flag: '🇮🇳' },
  { code: 'PK', name: 'Pakistan (+92)', flag: '🇵🇰' },
  { code: 'BD', name: 'Bangladesh (+880)', flag: '🇧🇩' },
];

interface ContactInfoEditPopupProps {
  open: boolean;
  onClose: () => void;
  onUpdate: (updatedData: Partial<VendorProfile>) => void;
  vendorProfile: VendorProfile | null;
}

const ContactInfoEditPopup: React.FC<ContactInfoEditPopupProps> = ({
  open,
  onClose,
  onUpdate,
  vendorProfile
}) => {
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('QA'); // Default to Qatar
  const [email, setEmail] = useState('');
  const [contactName, setContactName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form with current vendor data
  useEffect(() => {
    if (vendorProfile) {
      setPhone(vendorProfile.phone || '');
      setCountryCode(vendorProfile.countryCode || 'QA');
      setEmail(vendorProfile.email || '');
      setContactName(vendorProfile.contactName || '');
    }
  }, [vendorProfile, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!vendorProfile) {
      toast.error('No vendor session found. Please try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await vendorProfileService.editProfile(vendorProfile.vendorId, {
        phone: phone.trim(),
        countryCode: countryCode.trim(),
        // email: email.trim(), // Disabled - email cannot be changed
        contactName: contactName.trim(),
      });

      if (response.success) {
        toast.success('Contact information updated successfully!');
        onUpdate({
          phone: phone.trim(),
          countryCode: countryCode.trim(),
          email: email.trim(),
          contactName: contactName.trim(),
        });
        handleClose();
      } else {
        toast.error(response.message || 'Failed to update contact information');
      }
    } catch (error: any) {
      console.error('Error updating contact info:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          'Failed to update contact information. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Typography variant="h6" sx={{ 
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          fontWeight: 700,
          fontSize: '20px',
          color: '#181818'
        }}>
          Edit Contact Information
        </Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            py: 2
          }}
        >
          {/* Phone Field */}
          <TextField
            fullWidth
            placeholder="Phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: phone ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />

          {/* Country Code Field */}
          <FormControl
            fullWidth
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: '#181818'
              },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }
            }}
          >
            <InputLabel
              sx={{
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: '#93989c',
                '&.Mui-focused': {
                  color: '#93989c'
                }
              }}
            >
              Country Code
            </InputLabel>
            <Select
              value={countryCode}
              onChange={(e) => setCountryCode(e.target.value)}
              label="Country Code"
              MenuProps={{
                PaperProps: {
                  sx: {
                    borderRadius: '12px',
                    fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif'
                  }
                }
              }}
            >
              {countryCodes.map((country) => (
                <MenuItem
                  key={country.code}
                  value={country.code}
                  sx={{
                    fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                    fontSize: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <span>{country.flag}</span>
                  {country.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Email Field - Disabled */}
          <TextField
            fullWidth
            placeholder="Email address"
            value={email}
            disabled
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f0f0f0',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: '#93989c',
                cursor: 'not-allowed',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              },
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: '#93989c',
                color: '#93989c'
              }
            }}
            helperText="Email cannot be changed at this time"
            FormHelperTextProps={{
              sx: {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '12px',
                color: '#93989c',
                marginLeft: 0,
                marginTop: 1
              }
            }}
          />

          {/* Contact Name Field */}
          <TextField
            fullWidth
            placeholder="Contact person name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#f6f8f8',
                borderRadius: '12px',
                height: '56px',
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              },
              '& .MuiInputBase-input': {
                fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: '16px',
                color: contactName ? '#181818' : '#93989c',
                '&::placeholder': {
                  color: '#93989c',
                  opacity: 1
                }
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            width: '100%'
          }}
        >
          {/* Update Button */}
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: '#67314f',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a2a42'
              },
              '&:disabled': {
                backgroundColor: '#93989c',
                color: 'white'
              }
            }}
          >
            {isSubmitting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} sx={{ color: 'white' }} />
                Updating...
              </Box>
            ) : (
              'Update Contact Information'
            )}
          </Button>

          {/* Cancel Button */}
          <Button
            onClick={handleClose}
            disabled={isSubmitting}
            fullWidth
            sx={{
              height: '56px',
              backgroundColor: 'transparent',
              borderRadius: '16px',
              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
              fontWeight: 700,
              fontSize: '16px',
              letterSpacing: '-0.32px',
              textTransform: 'none',
              color: '#67314f',
              '&:hover': {
                backgroundColor: 'rgba(103, 49, 79, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ContactInfoEditPopup;
